import { ApplicationController } from "./application_controller"

export default class extends ApplicationController {
  static themes = {
    LIGHT: 'light-orange',
    SYSTEM: 'system',
    DARK: 'dark',
    STORAGE_KEY: 'theme'
  }

  // Map segment data-theme-toggle values to internal theme keys
  static segmentMap = {
    'light': 'light-orange',
    'system': 'system',
    'dark': 'dark'
  }

  connect() {
    this.iconElement = document.getElementById('theme-toggle-icon')
    this.labelElement = document.getElementById('theme-toggle-label')

    this.initializeTheme()
    this.setupSystemPreferenceListener()
    this.setupToggleButton()
  }

  disconnect() {
    if (window.__darkModeToggleHandler) {
      document.removeEventListener('click', window.__darkModeToggleHandler, true)
      window.__darkModeToggleHandler = null
    }
    if (this.mediaQuery && this.mediaQueryHandler) {
      this.mediaQuery.removeEventListener('change', this.mediaQueryHandler)
    }
  }

  setupToggleButton() {
    if (!window.__darkModeToggleHandler) {
      window.__darkModeToggleHandler = (e) => {
        const btn = e.target.closest('[data-theme-toggle]')
        if (btn) {
          e.preventDefault()
          e.stopPropagation()
          const segmentValue = btn.getAttribute('data-theme-toggle')
          if (segmentValue && this.constructor.segmentMap[segmentValue]) {
            this.setTheme(this.constructor.segmentMap[segmentValue])
          } else {
            this.toggle()
          }
        }
      }
      document.addEventListener('click', window.__darkModeToggleHandler, true)
    }
  }

  initializeTheme() {
    const storedTheme = localStorage.getItem(this.constructor.themes.STORAGE_KEY)
    const theme = storedTheme || this.constructor.themes.SYSTEM
    this.applyTheme(theme)
  }

  setTheme(theme) {
    localStorage.setItem(this.constructor.themes.STORAGE_KEY, theme)
    this.applyTheme(theme)
  }

  applyTheme(theme) {
    const themes = this.constructor.themes
    document.documentElement.classList.remove('dark')
    document.documentElement.removeAttribute('data-theme')
    document.documentElement.removeAttribute('data-mode')

    let effectiveTheme = theme
    if (theme === themes.SYSTEM) {
      effectiveTheme = this.getSystemPreference()
    }

    const normalizedMode = (theme === themes.SYSTEM || theme === themes.DARK) ? theme : 'light'
    document.documentElement.setAttribute('data-mode', normalizedMode)

    if (effectiveTheme === themes.DARK) {
      document.documentElement.classList.add('dark')
      document.documentElement.setAttribute('data-theme', themes.DARK)
      document.documentElement.style.colorScheme = 'dark'
    } else {
      document.documentElement.setAttribute('data-theme', themes.LIGHT)
      document.documentElement.style.colorScheme = 'light'
    }

    this.updateToggleUI(theme)
  }

  updateToggleUI(mode) {
    const themes = this.constructor.themes

    // Update segmented control active states
    const segments = document.querySelectorAll('.theme-segment')
    segments.forEach(seg => {
      seg.classList.remove('bg-base-100', 'shadow-sm', 'text-base-content')
      seg.classList.add('text-base-content/50', 'hover:text-base-content/80')
    })

    let activeId
    if (mode === themes.LIGHT) {
      activeId = 'theme-seg-light'
    } else if (mode === themes.DARK) {
      activeId = 'theme-seg-dark'
    } else {
      activeId = 'theme-seg-system'
    }

    const active = document.getElementById(activeId)
    if (active) {
      active.classList.remove('text-base-content/50', 'hover:text-base-content/80')
      active.classList.add('bg-base-100', 'shadow-sm', 'text-base-content')
    }
  }

  toggle() {
    const themes = this.constructor.themes
    const currentTheme = localStorage.getItem(themes.STORAGE_KEY) || themes.SYSTEM

    let newTheme
    if (currentTheme === themes.LIGHT) {
      newTheme = themes.SYSTEM
    } else if (currentTheme === themes.SYSTEM) {
      newTheme = themes.DARK
    } else {
      newTheme = themes.LIGHT
    }

    this.setTheme(newTheme)
  }

  getSystemPreference() {
    try {
      return window.matchMedia('(prefers-color-scheme: dark)').matches
        ? this.constructor.themes.DARK
        : this.constructor.themes.LIGHT
    } catch {
      return this.constructor.themes.LIGHT
    }
  }

  setupSystemPreferenceListener() {
    try {
      this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      this.mediaQueryHandler = () => {
        const currentTheme = localStorage.getItem(this.constructor.themes.STORAGE_KEY) || this.constructor.themes.SYSTEM
        if (currentTheme === this.constructor.themes.SYSTEM) {
          this.applyTheme(currentTheme)
        }
      }
      this.mediaQuery.addEventListener('change', this.mediaQueryHandler)
    } catch {
      // Media query not supported
    }
  }
}
